/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { renderHomePageHeader } from "../../assets/component/renderHomePageHeader.js";
import HomepageMenuCn from "../../assets/component/HomepageMenuCn/HomepageMenuCn.js";
import FaqSections from "../../assets/component/FaqSections/FaqSections.js";

const GROUPS = [
  {
    id: "start",
    title: "开始使用",
    note: "开立账户及正式上线。",
    items: [
      {
        q: "CheckPlus 是什么？",
        a: "CheckPlus 是专为非营利机构而设的香港付款及捐款平台。机构可获得网上账户、付款专页及自动收据功能，无需自行开发或维护系统，即可收取付款和捐款。",
      },
      {
        q: "哪些机构可以登记？",
        a: "在香港注册的慈善机构，包括根据《税务条例》第 88 条获豁免缴税的机构，以及需要收取付款或捐款的学校、教会、社团和社会企业。",
      },
      {
        q: "设置需要多长时间？",
        a: "大部分机构可于同日开立账户并发布首个付款或捐款专页。机构及银行资料的核实会与支付服务供应商同步进行，一般于数个工作日内完成。",
      },
      {
        q: "我们需要技术人员吗？",
        a: "不需要。专页、付款项目、捐款计划及收据全部可在 CheckPlus 账户内设置，无需安装软件，也无需编写代码。",
      },
      {
        q: "需要准备什么资料？",
        a: "机构注册文件、接收款项的银行账户、授权联络人，以及付款专页上希望显示的文字和图片。",
      },
    ],
  },
  {
    id: "payments",
    title: "付款与费用",
    note: "付款方式、平台费用及款项结算。",
    items: [
      {
        q: "支持哪些付款方式？",
        a: "所有计划均可通过 Stripe 接受 Visa、MasterCard、American Express 及银联。Apple Pay、Google Pay 及 PayMe for Business 适用于 Pro 计划，也可视账户情况启用其他本地及内地付款方式。",
      },
      {
        q: "CheckPlus 收费如何？",
        a: (
          <span>
            {
              "已在香港注册的慈善机构可免费使用 Free 计划（HK$0）。Plus 计划按年付款为每月 HK$459（按月付款为 HK$550），Pro 计划按年付款为每月 HK$792（按月付款为 HK$950）。详情请参阅"
            }
            <Link to="/CnPricing">价格页面</Link>
            {"。"}
          </span>
        ),
      },
      {
        q: "平台费用是多少？",
        a: "Free 计划的平台费用为 2%，Plus 及 Pro 计划为 1%。支付处理费用由支付网关另行收取。",
      },
      {
        q: "捐款人可以代付交易费用吗？",
        a: "可以。所有计划均可让捐款人选择代付交易费用，让机构收到完整的预期金额。",
      },
      {
        q: "款项何时到达机构银行账户？",
        a: "款项由支付服务供应商按其标准结算周期直接存入机构的银行账户。CheckPlus 不会代持款项。",
      },
      {
        q: "可以接受每月定期捐款吗？",
        a: "可以。Pro 计划支持每月定期交易，每次成功付款也会自动发出收据。",
      },
    ],
  },
  {
    id: "receipts",
    title: "捐款与收据",
    note: "收据、报表及捐款人记录。",
    items: [
      {
        q: "收据会自动发出吗？",
        a: "会。所有计划在交易成功后，均会自动生成并以电邮发送付款及捐款收据。",
      },
      {
        q: "收据可以使用机构品牌吗？",
        a: "可以。收据会显示机构名称及资料，Plus 及 Pro 计划更可进一步自定义付款专页及收据版面。",
      },
      {
        q: "可以发出可扣税捐款收据吗？",
        a: "如机构根据第 88 条获豁免缴税，CheckPlus 可发出载有香港捐款人报税所需资料的捐款收据。收据内容的准确性仍由机构负责。",
      },
      {
        q: "可以重新发出或更正收据吗？",
        a: "可以。收据可重新发送给捐款人，也可在账户内重新发出已更正的记录，确保报表准确。",
      },
      {
        q: "会提供哪些报表？",
        a: "Plus 及 Pro 计划可生成涵盖交易、捐款人及捐款计划的系统报表，并可导出作财务及审计用途。",
      },
    ],
  },
  {
    id: "security",
    title: "安全与隐私",
    note: "我们如何处理机构及捐款人的资料。",
    items: [
      {
        q: "资料储存在哪里？",
        a: "资料储存于 Amazon Web Services 的基础设施，并在传输及储存时加密。",
      },
      {
        q: "CheckPlus 会储存信用卡号码吗？",
        a: "不会。信用卡资料由已取得 PCI-DSS Level 1 认证的 Stripe 收集及处理，完整卡号不会进入 CheckPlus 系统。",
      },
      {
        q: "机构的捐款人资料会被出售吗？",
        a: (
          <span>
            {
              "不会。CheckPlus 承诺在未获特定同意的情况下，绝不出售或分享用户资料给任何第三方公司。详情请参阅"
            }
            <Link to="/CnPrivacy">我们的隐私承诺</Link>
            {"。"}
          </span>
        ),
      },
      {
        q: "团队成员可以看到什么？",
        a: "现阶段每间机构只有一个账户，所有登录的人看到的资料相同。团队账户（每位成员独立登录及权限）即将推出。",
      },
    ],
  },
  {
    id: "account",
    title: "账户与支持",
    note: "更改计划、语言及获取协助。",
    items: [
      {
        q: "之后可以更改计划吗？",
        a: "升级可随时进行。降级会于现有计费周期结束时生效，因此如选用按年付款，须待年度结束后才会转换。",
      },
      {
        q: "CheckPlus 有中文版本吗？",
        a: "有。平台及对外的付款专页均提供英文、繁体中文及简体中文，多语言专页包含于 Pro 计划。",
      },
      {
        q: "如何获得支持？",
        a: (
          <span>
            {"请于"}
            <Link to="/CnContact">联络页面</Link>
            {
              "与我们的香港团队联络。我们通常会在 2 至 4 个工作日内回复。"
            }
          </span>
        ),
      },
    ],
  },
];

class CnFaq extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
    };
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  showModal() {
    this.setState({
      showModal: true,
    });
  }

  closeModal() {
    this.setState({
      showModal: false,
    });
  }

  render() {
    return (
      <div className="Faq-view">
        <Helmet>
          <title>常见问题 - CheckPlus</title>
        </Helmet>
        {!!this.state.showModal && (
          <HomepageMenuCn closeModal={this.closeModal} />
        )}
        {renderHomePageHeader(this)}

        <FaqSections
          title="有什么可以帮到你？"
          sub="香港非营利机构最常问我们的问题——关于开立账户、付款方式、费用、收据，以及如何保护捐款人资料。"
          searchPlaceholder="搜索常见问题……"
          emptyText="找不到符合的答案。请尝试其他关键词，或在下方联络我们。"
          groups={GROUPS}
          cta={{
            title: "还有其他问题？",
            txt: "我们的香港团队通常会在 2 至 4 个工作日内回复。",
            primary: "联络我们",
            to: "/CnContact",
          }}
        />

        <div className="Faq-footer">
          <div className="Faq-footerNav">
            <Link to="/CnTeam">
              <div className="Faq-footerNavItem">创办人的故事</div>
            </Link>

            <Link to="/CnFaq">
              <div className="Faq-footerNavItem">常见问题</div>
            </Link>

            <Link to="/CnContact">
              <div className="Faq-footerNavItem">联络我们</div>
            </Link>

            <Link to="/CnPrivacyPolicy">
              <div className="Faq-footerNavItem">私隐政策</div>
            </Link>

            <Link to="/CnTerms">
              <div className="Faq-footerNavItem">使用条款</div>
            </Link>
          </div>

          <div className="Faq-footer_text">
            香港九龙油塘四山街 11 号
            <br />
            华利工业大厦5楼A室
            <br />
            <p />
            Copyright © 2026 CheckPlus Ltd. 保留一切权利。
          </div>
        </div>
      </div>
    );
  }
}

export default CnFaq;
